import { createFrontOfficeInstance } from '@/shared/api/api-instance';

export type AiKnowledgeBaseFreshness = 'fresh' | 'review_due' | 'expired';
export type AiKnowledgeBaseSourceType = 'manual' | 'document' | 'url';
export type AiKnowledgeBaseImportSourceType = 'text' | 'url';
export type AiKnowledgeBaseAiMode = 'suggest' | 'assist' | 'autopilot';

export type AiKnowledgeBaseApplicabilityDto = {
  channel_ids: string[];
  crm_connection_ids: string[];
  pipeline_ids: string[];
  user_role_ids: string[];
  scenarios: string[];
  ai_modes: AiKnowledgeBaseAiMode[];
};

export type UpdateAiKnowledgeBaseApplicabilityDto =
  Partial<AiKnowledgeBaseApplicabilityDto>;

export type AiKnowledgeBaseEntryDto = {
  id: string;
  account_id: string;
  company_id: string;
  title: string;
  content: string;
  tags: string[];
  enabled: boolean;
  company_prompt: string | null;
  forbidden_topics: string[];
  handoff_rules: string[];
  source_type: AiKnowledgeBaseSourceType;
  source_url: string | null;
  source_title: string | null;
  freshness: AiKnowledgeBaseFreshness;
  reviewed_at: string | null;
  expires_at: string | null;
  version: number;
  created_by: string | null;
  updated_by: string | null;
  applicability: AiKnowledgeBaseApplicabilityDto;
  created_at: string;
  updated_at: string;
};

export type AiKnowledgeBaseEntriesResponseDto = {
  entries: AiKnowledgeBaseEntryDto[];
  total: number;
  active_count: number;
};

export type AiKnowledgeBaseEntriesFiltersDto = {
  query?: string;
  tag?: string;
  enabled?: boolean;
  freshness?: AiKnowledgeBaseFreshness;
  limit?: number;
};

export type CreateAiKnowledgeBaseEntryRequestDto = {
  title: string;
  content: string;
  tags?: string[];
  enabled?: boolean;
  company_prompt?: string | null;
  forbidden_topics?: string[];
  handoff_rules?: string[];
  source_type?: AiKnowledgeBaseSourceType;
  source_url?: string | null;
  source_title?: string | null;
  reviewed_at?: string | null;
  expires_at?: string | null;
  applicability?: AiKnowledgeBaseApplicabilityDto;
};

export type UpdateAiKnowledgeBaseEntryRequestDto = Partial<
  Omit<CreateAiKnowledgeBaseEntryRequestDto, 'applicability'>
> & {
  applicability?: UpdateAiKnowledgeBaseApplicabilityDto;
};

export type AiKnowledgeBaseUsageStatusDto = 'ok' | 'skipped' | 'failed';

export type AiKnowledgeBaseUsageItemDto = {
  activity_log_id: string;
  source: string;
  status: AiKnowledgeBaseUsageStatusDto;
  chat_id: string | null;
  channel_id: string | null;
  created_at: string;
};

export type AiKnowledgeBaseUsageResponseDto = {
  entry_id: string;
  items: AiKnowledgeBaseUsageItemDto[];
};

export type AiKnowledgeBaseSourceDto = {
  id: string;
  title: string;
  tags: string[];
};

export type AiKnowledgeBaseCoverageCheckRequestDto = {
  company_id: string;
  questions: string[];
};

export type AiKnowledgeBaseCoverageResultDto = {
  question: string;
  matched_sources: AiKnowledgeBaseSourceDto[];
  covered: boolean;
};

export type AiKnowledgeBaseCoverageResponseDto = {
  results: AiKnowledgeBaseCoverageResultDto[];
};

export type CreateAiKnowledgeBaseImportRequestDto = {
  source_type: AiKnowledgeBaseImportSourceType;
  title: string;
  content?: string;
  url?: string;
  tags?: string[];
  publish_enabled?: boolean;
};

export type AiKnowledgeBaseImportJobDto = {
  id: string;
  company_id: string;
  source_type: AiKnowledgeBaseImportSourceType;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  title: string;
  source_url: string | null;
  entry_id: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
};

const setOptionalParam = (
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | undefined
) => {
  if (value === undefined || value === '') return;
  params.set(key, String(value));
};

const withCompanyQuery = (
  companyId: string,
  filters: AiKnowledgeBaseEntriesFiltersDto = {}
) => {
  const params = new URLSearchParams();

  params.set('company_id', companyId);
  setOptionalParam(params, 'query', filters.query);
  setOptionalParam(params, 'tag', filters.tag);
  setOptionalParam(params, 'enabled', filters.enabled);
  setOptionalParam(params, 'freshness', filters.freshness);
  setOptionalParam(params, 'limit', filters.limit);

  return params.toString();
};

export const getAiKnowledgeBaseEntries = (
  companyId: string,
  filters: AiKnowledgeBaseEntriesFiltersDto = {}
) =>
  createFrontOfficeInstance<AiKnowledgeBaseEntriesResponseDto>({
    url: `/api/v1/ai/knowledge-base?${withCompanyQuery(companyId, filters)}`,
    method: 'GET',
  });

export const createAiKnowledgeBaseEntry = (
  companyId: string,
  data: CreateAiKnowledgeBaseEntryRequestDto
) =>
  createFrontOfficeInstance<AiKnowledgeBaseEntryDto>({
    url: `/api/v1/ai/knowledge-base?${withCompanyQuery(companyId)}`,
    method: 'POST',
    data,
  });

export const updateAiKnowledgeBaseEntry = (
  companyId: string,
  id: string,
  data: UpdateAiKnowledgeBaseEntryRequestDto
) =>
  createFrontOfficeInstance<AiKnowledgeBaseEntryDto>({
    url: `/api/v1/ai/knowledge-base/${id}?${withCompanyQuery(companyId)}`,
    method: 'PUT',
    data,
  });

export const deleteAiKnowledgeBaseEntry = (companyId: string, id: string) =>
  createFrontOfficeInstance<{ ok: true }>({
    url: `/api/v1/ai/knowledge-base/${id}?${withCompanyQuery(companyId)}`,
    method: 'DELETE',
  });

export const getAiKnowledgeBaseUsage = (companyId: string, id: string) =>
  createFrontOfficeInstance<AiKnowledgeBaseUsageResponseDto>({
    url: `/api/v1/ai/knowledge-base/${id}/usage?${withCompanyQuery(companyId)}`,
    method: 'GET',
  });

export const checkAiKnowledgeBaseCoverage = (
  companyId: string,
  questions: string[]
) =>
  createFrontOfficeInstance<AiKnowledgeBaseCoverageResponseDto>({
    url: '/api/v1/ai/knowledge-base/coverage-check',
    method: 'POST',
    data: {
      company_id: companyId,
      questions,
    } satisfies AiKnowledgeBaseCoverageCheckRequestDto,
  });

export const createAiKnowledgeBaseImport = (
  companyId: string,
  data: CreateAiKnowledgeBaseImportRequestDto
) =>
  createFrontOfficeInstance<AiKnowledgeBaseImportJobDto>({
    url: '/api/v1/ai/knowledge-base/imports',
    method: 'POST',
    data: {
      company_id: companyId,
      ...data,
    },
  });
